import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, X } from 'lucide-react';

interface VideoPlayerProps {
    videoUrl: string;
    onClose?: () => void;
    autoPlay?: boolean;
    showControls?: boolean;
    className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
    videoUrl,
    onClose,
    autoPlay = true,
    showControls = true,
    className = ''
}) => {
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isMuted, setIsMuted] = useState(false); // Default to unmuted – we'll only mute if autoplay fails
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [showFullscreenButton, setShowFullscreenButton] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handleLoadedMetadata = () => {
            setDuration(video.duration);
            setIsLoading(false);
        };
        const handleEnded = () => {
            setIsPlaying(false);
            // Auto-close after 2 seconds like mobile app
            setTimeout(() => {
                if (onClose) {
                    onClose();
                }
            }, 2000);
        };
        const handleError = () => {
            setError('Failed to load video');
            setIsLoading(false);
        };
        const handleLoadStart = () => {
            setIsLoading(true);
            setError(null);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('error', handleError);
        video.addEventListener('loadstart', handleLoadStart);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('error', handleError);
            video.removeEventListener('loadstart', handleLoadStart);
        };
    }, [onClose]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (isPlaying) {
            video.play().catch((error) => {
                console.error('Video play failed:', error);
                // If auto-play fails (browser policy), try again muted
                if (autoPlay && !isMuted) {
                    video.muted = true;
                    setIsMuted(true);
                    video.play().catch(console.error);
                }
            });
        } else {
            video.pause();
        }
    }, [isPlaying, autoPlay, isMuted]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        video.muted = isMuted;
    }, [isMuted]);

    // Handle initial auto-play when component mounts
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !autoPlay) return;

        const handleCanPlay = () => {
            if (autoPlay && isPlaying) {
                video.play().catch((error) => {
                    console.error('Initial auto-play failed:', error);
                    // If auto-play fails, try with muted
                    if (!isMuted) {
                        video.muted = true;
                        setIsMuted(true);
                        video.play().catch(console.error);
                    }
                });
            }
        };

        video.addEventListener('canplay', handleCanPlay);
        return () => video.removeEventListener('canplay', handleCanPlay);
    }, [autoPlay, isPlaying, isMuted]);

    const togglePlay = () => {
        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const video = videoRef.current;
        if (!video) return;

        const newTime = (parseFloat(e.target.value) / 100) * duration;
        video.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <div
            ref={containerRef}
            className={`relative bg-black rounded-lg overflow-hidden ${className}`}
        >
            <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-full object-contain"
                autoPlay={autoPlay}
                muted={isMuted}
                playsInline
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            />

            {/* Loading Overlay */}
            {isLoading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-4xl mb-2">⏳</div>
                        <div className="text-white text-lg">Loading video...</div>
                    </div>
                </div>
            )}

            {/* Error Overlay */}
            {error && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-4xl mb-2">❌</div>
                        <div className="text-white text-lg">{error}</div>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {/* Close Button - Always Visible */}
            {showControls && onClose && (
                <div className="absolute top-4 right-4 z-10">
                    <button
                        onClick={handleClose}
                        className="p-2 bg-black/70 hover:bg-black/90 text-white rounded-full transition-colors shadow-lg"
                        title="Close video"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}

            {/* Overlay Controls */}
            {showControls && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
                    {/* Top Controls */}
                    <div className="absolute top-4 right-4 flex items-center space-x-2">
                        {showFullscreenButton && (
                            <button
                                onClick={toggleFullscreen}
                                className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                            >
                                <Maximize className="w-5 h-5" />
                            </button>
                        )}
                    </div>

                    {/* Center Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button
                            onClick={togglePlay}
                            className="p-4 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                        >
                            {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
                        </button>
                    </div>

                    {/* Bottom Controls */}
                    <div className="absolute bottom-4 left-4 right-4">
                        {/* Progress Bar */}
                        <div className="mb-3">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                                onChange={handleSeek}
                                className="w-full h-2 bg-white/30 rounded-lg appearance-none cursor-pointer slider"
                                style={{
                                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${duration > 0 ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.3) ${duration > 0 ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.3) 100%)`
                                }}
                            />
                        </div>

                        {/* Control Buttons */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={togglePlay}
                                    className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                                >
                                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                                </button>

                                <button
                                    onClick={toggleMute}
                                    className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
                                >
                                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </button>

                                <span className="text-white text-sm">
                                    {formatTime(currentTime)} / {formatTime(duration)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Slider Styles */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .slider::-webkit-slider-thumb {
                  appearance: none;
                  height: 16px;
                  width: 16px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                }
                
                .slider::-moz-range-thumb {
                  height: 16px;
                  width: 16px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                }
              `
            }} />
        </div>
    );
};

export default VideoPlayer;
